import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { DollarSign, AlertTriangle, CheckCircle2, Plus } from "lucide-react";

const KNOWN_PROVIDERS = ["openai", "anthropic", "google", "mistral"];

type Budget = {
  id: string;
  provider_name: string;
  monthly_budget_usd: number;
  alert_threshold_pct: number;
  current_spend_usd: number;
  spend_period_key: string;
  active: boolean;
};

type Health = {
  provider_name: string;
  status: string;
  cooldown_until: string | null;
};

export function ProviderBudgetsTab({ orgId }: { orgId: string | null }) {
  const queryClient = useQueryClient();
  const [newProvider, setNewProvider] = useState("");

  const { data: budgets = [] } = useQuery({
    queryKey: ["provider-budgets", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("provider_budgets")
        .select("*")
        .eq("org_id", orgId!)
        .order("provider_name");
      if (error) throw error;
      return data as Budget[];
    },
  });

  const { data: health = [] } = useQuery({
    queryKey: ["provider-health", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("provider_health")
        .select("provider_name, status, cooldown_until")
        .eq("org_id", orgId!);
      if (error) throw error;
      return data as Health[];
    },
    refetchInterval: 30000,
  });

  const upsertMut = useMutation({
    mutationFn: async (b: Partial<Budget> & { provider_name: string }) => {
      if (!orgId) throw new Error("Sem organização ativa");
      const { error } = await supabase
        .from("provider_budgets")
        .upsert({ org_id: orgId, ...b }, { onConflict: "org_id,provider_name" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-budgets"] });
      toast.success("Orçamento salvo");
      setNewProvider("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const healthMap = new Map(health.map((h) => [h.provider_name.toLowerCase(), h]));

  function statusBadge(name: string) {
    const h = healthMap.get(name.toLowerCase());
    if (!h || h.status === "ok") return <Badge variant="outline" className="text-emerald-600 border-emerald-600/30"><CheckCircle2 className="w-3 h-3 mr-1" /> OK</Badge>;
    const labels: Record<string, string> = {
      rate_limited: "Rate Limited",
      budget_exceeded: "Budget Esgotado",
      slow: "Lento",
      down: "Indisponível",
    };
    return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" /> {labels[h.status] || h.status}</Badge>;
  }

  const existingNames = new Set(budgets.map((b) => b.provider_name.toLowerCase()));
  const availableToAdd = KNOWN_PROVIDERS.filter((p) => !existingNames.has(p));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Orçamento Mensal por Provedor
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Quando o orçamento de um provedor é esgotado (ou ele retorna 402/429), o gateway pula automaticamente para o próximo modelo da cadeia de fallback configurada na política.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {budgets.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum orçamento configurado. Adicione um provedor abaixo para começar a controlar gastos.
            </p>
          )}

          {budgets.map((b) => {
            const pct = Math.min(100, Math.round((b.current_spend_usd / Math.max(0.01, b.monthly_budget_usd)) * 100));
            const isOver = pct >= 100;
            const isWarn = pct >= b.alert_threshold_pct;
            return (
              <div key={b.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium capitalize">{b.provider_name}</span>
                    {statusBadge(b.provider_name)}
                  </div>
                  <Switch
                    checked={b.active}
                    onCheckedChange={(v) => upsertMut.mutate({ provider_name: b.provider_name, active: v })}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2 items-end">
                  <div>
                    <Label className="text-xs">Orçamento (USD/mês)</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      defaultValue={b.monthly_budget_usd}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v !== Number(b.monthly_budget_usd)) {
                          upsertMut.mutate({ provider_name: b.provider_name, monthly_budget_usd: v });
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Alerta em %</Label>
                    <Input
                      type="number"
                      step="5"
                      min="10"
                      max="100"
                      defaultValue={b.alert_threshold_pct}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value);
                        if (!isNaN(v) && v !== b.alert_threshold_pct) {
                          upsertMut.mutate({ provider_name: b.provider_name, alert_threshold_pct: v });
                        }
                      }}
                    />
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Gasto este mês</p>
                    <p className="text-lg font-bold">${Number(b.current_spend_usd).toFixed(4)}</p>
                  </div>
                </div>

                <div className="space-y-1">
                  <Progress value={pct} className={isOver ? "[&>div]:bg-destructive" : isWarn ? "[&>div]:bg-orange-500" : ""} />
                  <p className="text-xs text-muted-foreground">
                    {pct}% utilizado · período {b.spend_period_key}
                  </p>
                </div>
              </div>
            );
          })}

          {availableToAdd.length > 0 && (
            <div className="flex gap-2 pt-2 border-t">
              <select
                className="flex h-9 rounded-md border bg-background px-3 text-sm"
                value={newProvider}
                onChange={(e) => setNewProvider(e.target.value)}
              >
                <option value="">Adicionar provedor…</option>
                {availableToAdd.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <Button
                size="sm"
                disabled={!newProvider || upsertMut.isPending}
                onClick={() => upsertMut.mutate({ provider_name: newProvider, monthly_budget_usd: 50, alert_threshold_pct: 80, active: true })}
              >
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
